import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { federationsAPI } from '../services/api';

// Avatar avec initiales si pas de photo
const AvatarWithInitials = ({ name, photo, className = "w-32 h-32" }: { name: string; photo?: string; className?: string }) => {
  if (photo && photo.trim() !== "") {
    return <img src={photo} alt={name} className={`${className} rounded-full object-cover border-4 border-white shadow-lg`} />;
  }

  const initials = name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className={`${className} rounded-full flex items-center justify-center text-white font-bold text-3xl bg-gradient-to-br from-blue-600 to-teal-500 border-4 border-white shadow-2xl`}>
      {initials}
    </div>
  );
};

const Federations = () => {
  const { t, language } = useLanguage();
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [federations, setFederations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // ==================== BUREAU EXÉCUTIF ====================
  const president = { name: "Oumar SEYE", title: "Président", photo: "//ascsurf.com/app/assets/images/8.png" };

  const vicePresidents = [
    { name: "Johnny BAKKER", title: "1er Vice-Président", commission: "Commission Juridique et Diplomatie de l’ASC", photo: "//ascsurf.com/app/assets/images/1-1.png" },
    { name: "Naquitta RICKS", title: "2ème Vice-Présidente", commission: "Partenariats, Relations Publiques", photo: "" },
    { name: "Prince Adewale FAWE", title: "3ème Vice-Président", commission: "Porte-parole de l’ASC", photo: "//ascsurf.com/app/assets/images/6.png" },
    { name: "Roger FAUSTINATO", title: "4ème Vice-Président", commission: "Organisation et Protocole", photo: "//ascsurf.com/app/assets/images/2-1.png" },
  ];

  const secretariat = [
    { name: "Razak Dourodjaye GBADAMASSI", title: "Secrétaire Général", photo: "//ascsurf.com/app/assets/images/razak.jpeg" },
    { name: "Wendy YOUNG", title: "Secrétaire Générale Adjointe", photo: "//ascsurf.com/app/assets/images/3-1.png" },
  ];

  const tresorerie = { name: "Mamadou DIOUF", title: "Trésorier", photo: "//ascsurf.com/app/assets/images/Untitled-design.png" };

  const membresElus = [
    { name: "Marcelino CORREIA DE BARROS", title: "Fédération Angolaise de Surf", photo: "" },
    { name: "Sylvain KOUAO", title: "Vice-Président Fédération Ivoirienne de Surf", photo: "//ascsurf.com/app/assets/images/sylvain.jpeg" },
    { name: "Alexandre ALCANTARA", title: "Président Fédération Sénégalaise de Surf", photo: "//ascsurf.com/app/assets/images/Untitled-design-2.png" },
  ];

  const commissaireMarketing = [
    { name: "Souleye MBENGUE", title: "Commissaire aux Comptes", commission: "Responsable Projet, Formation et Développement", photo: "//ascsurf.com/app/assets/images/Untitled-design-1.png" },
    { name: "Nadi SADDY", title: "Commission Marketing", photo: "" },
    { name: "Bengaly CAMARA", title: "Commission Marketing", photo: "" },
    { name: "Aliou GUEYE", title: "Cameraman Officiel", photo: "" },
  ];

  const commissionTechnique = [
    { name: "Ludo", title: "Formateur et Développement ISA Afrique", photo: "" },
    { name: "Shafiek KHAN", title: "Directeur Technique Afrique du Sud", photo: "" },
    { name: "Gebahi WALID", title: "Team Manager", photo: "" },
    { name: "Adnane BENSLIMANE", title: "Team Manager Bodyboard – Responsable Formation & DT Afrique", country: "Maroc", photo: "//ascsurf.com/app/assets/images/adnane.jpeg" },
    { name: "Marta IMARISIO", title: "Responsable Développement Surf Féminin Africain", photo: "//ascsurf.com/app/assets/images/marta.jpeg" },
  ];

  useEffect(() => {
    const fetchFederations = async () => {
      try {
        setLoading(true);
        const params: any = { lang: language };
        if (selectedRegion !== 'all') params.region = selectedRegion;
        const response = await federationsAPI.getAll(params);
        setFederations(response.records || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchFederations();
  }, [selectedRegion, language]);

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Hero + Grille fédérations */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-teal-800 to-blue-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="https://i.imgur.com/mrt24S8.jpeg?auto=compress&cs=tinysrgb&w=1600" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">{t('federations.hero.title') || 'Fédérations Membres'}</h1>
          <div className="text-3xl font-bold text-orange-400">{federations.length} fédérations</div>
        </div>
      </section>

      {/* Federations Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {federations.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">Aucune fédération trouvée pour cette région.</p>
              <button 
                onClick={() => setSelectedRegion('all')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
              >
                Voir toutes les régions
              </button>
            </div>
          ) : (
           <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
  {federations.map((federation) => (
    <div
      key={federation.id}
      className="bg-white/10 backdrop-blur-md rounded-xl p-6 text-center shadow-md hover:shadow-xl transition-all duration-300"
    >
      <img
        src={federation.flag}
        alt={`Drapeau de ${federation.country}`}
        className="w-16 h-16 object-contain mx-auto mb-4 rounded-full border border-white shadow-sm"
      />
      <h3 className="text-lg font-semibold text-black">{federation.country}</h3>
    </div>
  ))}
</div>

          )}
        </div>
      </section>

      {/* ==================== BUREAU EXÉCUTIF ==================== */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold">Bureau Exécutif ASC</h2>
          </div>

          {/* Président */}
          <div className="flex justify-center mb-24">
            <div className="text-center">
              <AvatarWithInitials name={president.name} photo={president.photo} className="w-56 h-56 mx-auto" />
              <h3 className="mt-10 text-4xl font-bold">{president.name}</h3>
              <p className="text-orange-400 text-3xl font-semibold">{president.title}</p>
            </div>
          </div>

          {/* Vice-Présidents */}
          <div className="mb-24">
            <h3 className="text-4xl font-bold text-center mb-14">Vice-Présidents et Commissions</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
              {vicePresidents.map((vp, i) => (
                <div key={i} className="flex flex-col items-center bg-white/10 p-10 rounded-3xl border border-white/20 text-center">
                  <AvatarWithInitials name={vp.name} photo={vp.photo} className="w-40 h-40" />
                  <h4 className="mt-8 text-2xl font-bold">{vp.name}</h4>
                  <p className="text-orange-400 text-lg">{vp.title}</p>
                  <p className="text-sm text-gray-300 mt-4 leading-relaxed">{vp.commission}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Secrétariat */}
          <div className="mb-24">
            <h3 className="text-4xl font-bold text-center mb-14">Secrétariat</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 max-w-5xl mx-auto">
              {secretariat.map((s, i) => (
                <div key={i} className="text-center bg-white/10 p-12 rounded-3xl">
                  <AvatarWithInitials name={s.name} photo={s.photo} className="w-48 h-48 mx-auto" />
                  <p className="mt-8 font-bold text-2xl">{s.name}</p>
                  <p className="text-blue-300 text-xl mt-2">{s.title}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Trésorerie */}
          <div className="mb-24">
            <h3 className="text-4xl font-bold text-center mb-14">Trésorerie</h3>
            <div className="flex justify-center">
              <div className="text-center bg-white/10 p-16 rounded-3xl border-4 border-green-500/30 max-w-md">
                <AvatarWithInitials name={tresorerie.name} photo={tresorerie.photo} className="w-56 h-56 mx-auto" />
                <p className="mt-10 font-bold text-3xl">{tresorerie.name}</p>
                <p className="text-green-400 text-2xl mt-3">{tresorerie.title}</p>
              </div>
            </div>
          </div>

          {/* Membres Élus */}
          <div className="mb-24">
            <h3 className="text-4xl font-bold text-center mb-14">Trois (03) Membres Élus selon les statuts</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-6xl mx-auto">
              {membresElus.map((m, i) => (
                <div key={i} className="text-center bg-white/10 p-10 rounded-3xl border border-white/20">
                  <AvatarWithInitials name={m.name} photo={m.photo} className="w-44 h-44 mx-auto" />
                  <p className="mt-8 font-bold text-2xl">{m.name}</p>
                  <p className="text-gray-300 text-lg mt-3">{m.title}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Commissaire aux Comptes & Marketing */}
          <div className="mb-24">
            <h3 className="text-4xl font-bold text-center mb-14">Commissaire aux Comptes & Marketing</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
              {commissaireMarketing.map((p, i) => (
                <div key={i} className="text-center bg-white/10 p-8 rounded-3xl">
                  <AvatarWithInitials name={p.name} photo={p.photo} className="w-40 h-40 mx-auto" />
                  <p className="mt-6 font-bold text-xl">{p.name}</p>
                  <p className="text-purple-300 text-lg">{p.title}</p>
                  {p.commission && <p className="text-xs text-gray-400 mt-3 italic">{p.commission}</p>}
                </div>
              ))}
            </div>
          </div>

          {/* Commission Technique */}
          <div>
            <h3 className="text-4xl font-bold text-center mb-14">Commission Technique</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 max-w-7xl mx-auto">
              {commissionTechnique.map((t, i) => (
                <div key={i} className="text-center bg-white/10 p-8 rounded-3xl">
                  <AvatarWithInitials name={t.name} photo={t.photo} className="w-36 h-36 mx-auto" />
                  <p className="mt-6 font-bold text-xl">{t.name}</p>
                  {t.country && <p className="text-xs text-gray-400">({t.country})</p>}
                  <p className="text-teal-300 text-sm mt-3">{t.title}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-5xl font-bold mb-6">Rejoignez l’ASC</h2>
          <a href="/contact" className="inline-block bg-white text-blue-600 hover:bg-gray-100 px-12 py-5 rounded-full font-bold text-xl transition transform hover:scale-105 shadow-2xl">
            Nous contacter
          </a>
        </div>
      </section>
    </div>
  );
};

export default Federations;